import { supabase } from "@/lib/supabase";
import { isLinguaproz } from "@/config/mode";

// Every call to the machine translation proxy (/api/translate/*) goes through mtFetch.
//   - It sends the login token: the Supabase session for TranslyxCAT, the lp_token for
//     LinguaProz translators.
//   - 401: one session refresh and one retry, then MtSessionError.
//   - 402: the server says the plan (and any top-up pack) cannot cover the request. The
//     upgrade dialog is opened through MT_LIMIT_EVENT and MtLimitError is thrown.
// Usage itself is counted by the server, never by the browser.

export const MT_LIMIT_EVENT = "cat:mt-limit-reached";
export const MT_USAGE_CHANGED_EVENT = "cat:mt-usage-changed";
export const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

export class MtSessionError extends Error {
    constructor() {
        super(SESSION_EXPIRED_MESSAGE);
        this.name = "MtSessionError";
    }
}

export class MtLimitError extends Error {
    kind: "mt" | "ai";
    reason?: string;
    constructor(kind: "mt" | "ai", reason?: string) {
        super(
            kind === "ai"
                ? "You have reached your AI query limit for this month."
                : "You have reached your machine translation limit.",
        );
        this.name = "MtLimitError";
        this.kind = kind;
        this.reason = reason;
    }
}

async function currentToken(forceRefresh = false): Promise<string | null> {
    if (isLinguaproz) return localStorage.getItem("lp_token");
    if (forceRefresh) {
        const { data, error } = await supabase.auth.refreshSession();
        return error ? null : (data.session?.access_token ?? null);
    }
    // getSession renews an expired access token by itself when it can.
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
}

let usageTimer: ReturnType<typeof setTimeout> | undefined;
function notifyUsageChanged() {
    clearTimeout(usageTimer);
    usageTimer = setTimeout(
        () => window.dispatchEvent(new Event(MT_USAGE_CHANGED_EVENT)),
        400,
    );
}

export async function mtFetch(url: string, init: RequestInit = {}): Promise<Response> {
    const send = (token: string | null) =>
        fetch(url, {
            ...init,
            headers: {
                ...((init.headers as Record<string, string>) || {}),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

    let res = await send(await currentToken());

    if (res.status === 401) {
        // LinguaProz sessions cannot be refreshed here; the translator signs in again.
        const refreshed = isLinguaproz ? null : await currentToken(true);
        if (refreshed) res = await send(refreshed);
        if (res.status === 401) throw new MtSessionError();
    }

    if (res.status === 402) {
        const body = await res.clone().json().catch(() => ({}));
        const kind: "mt" | "ai" = url.includes("/api/translate/ai") ? "ai" : "mt";
        window.dispatchEvent(
            new CustomEvent(MT_LIMIT_EVENT, { detail: { kind, reason: body?.reason } }),
        );
        throw new MtLimitError(kind, body?.reason);
    }

    if (res.ok) notifyUsageChanged();
    return res;
}
